<template>
  <div class="poster vh100">
    <div class="flag"
      @click="() => { isWrapperVisible = true}">
      <img src="../assets/images/poster-corner-flag.png"/>
      <span>赚{{ earn }}</span>
    </div>

    <section class="stage">
      <!-- 生成的图片放置在该元素中 -->
      <img
        v-if="ready"
        :src="posterData"
        class="poster"
        alt="poster"
        @click.prevent
        ref="img"
        v-longclick="() => showShareNotice()"
      />

      <div class="posterMask" v-if="building">
        <img src="../assets/loading.svg" class="" alt="loading"/>
      </div>

      <!-- build 完成后隐藏画布 -->
      <div class="painter" ref="painter">
        <div :class="`user-${position}`">
          <img :src="avatarData" class="avatar" :class="`poster-${style}`" ref="avatar">
          <span class="username" :class="`poster-${position} ${pureCharacter}`" :style="{ color: posterStyle[position]}">{{ username }}</span>
        </div>
        <img :src="qrcodeData" class="qrcode" :class="`poster-${position}`"/>
        <img class="bg" :src="imgSrc" alt="img"/>
      </div>

      <p v-if="ready && !building">长按保存图片发送给好友</p>
    </section>

    <footer>
      <section class="controller">
        <div class="selector" :class="{ compact: templateList.length < 3 }">
          <div
            class="option"
            v-for="(item, index) in templateList"
            :key="index"
            :class="{ on: style === index }"
          >
          <div class="optionMask" @click.prevent="select(index)"/>
            <img
              class="border"
              src="../assets/images/poster-option-bg.png" alt="border"
              v-if="ready && style === index"/>

            <img class="thumbnail" :src="courseId ? item.imageUrl : ''" alt="option"/>
          </div>
        </div>
      </section>
      <p>好友扫你分享的二维码购买课程立减20元</p>
    </footer>

    <screen-wrapper
      v-if="isWrapperVisible"
      @close="() => isWrapperVisible = false">
      <div class="wrapper-image" />
    </screen-wrapper>

    <notice :closeBtn="true" ref="notice">
      <div class="share-box">
        <div class="share-title">分享内容</div>
        <div class="share-subtitle">加上文案效果更好哦~</div>
        <div class="share-content">{{shareText}}</div>
        <ui-button
          class="share-button"
          v-clipboard="() => shareText"
          v-clipboard:success="() => alert('复制成功')"
        >一键复制</ui-button>
      </div>
    </notice>
  </div>
</template>

<script>
import api from '@edu-fe/api'
import login from '@/utils/login'
import keys from 'lodash.keys'
import html2canvas from 'html2canvas'
import { longClickDirective } from 'vue-long-click'
import Clipboard from 'v-clipboard'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'

import ScreenWrapper from '../components/ScreenWrapper'
import Notice from '../components/Notice'
import UIButton from '../components/UI/Button'
import module from '../modules/poster'

const longClickInstance = longClickDirective({delay: 700, interval: 0})
Vue.directive('longclick', longClickInstance)
Vue.use(Clipboard)

const posterStyle = {
  0: ['#000', '#000', '#000', '#000'],
  1: ['#000', '#fff', '#fff', '#000', '卢菲菲海报'],
  2: ['#000', '#fff', '#fff', '#000', '老路海报'],
  3: ['#fff', '#fff', '#000', '#000', '高情商海报'],
  4: ['#000', '#fff', '#fff', '#000', '高情商海报']
}

export default {
  computed: {
    ...mapState(module.name, keys(module.state)),
    ...mapGetters(module.name, keys(module.getters)),

    pureCharacter () {
      return this.username.split('').every(item => /[\u4e00-\u9fa5]/.test(item)) ? 'pure' : 'notPure'
    },

    avatarData () {
      if (/^(https|http)/.test(this.avatar))
        return this.defaultAvatar

      return this.avatar
    },

    hasCache () {
      try {
        return this.posterCache[this.courseId][this.style].length
      } catch (e){
        return 0
      }
    }
  },

  data () {
    return {
      style: 0,
      imgSrc: '',
      posterStyle,
      posterCache: {},
      position: 0, // 样式布局
      defaultAvatar: require('../assets/images/logo.jpg'),
      isWrapperVisible: false
    }
  },

  watch: {
    ready: {
      async handler (n) {
        if (n) {
          this.style = 0
          this.position = this.templateList[0].position - 1
          this.imgSrc = await this.getBase64ByApi(this.templateList[0].imageUrl)
          await this.buildThumbnail()
        }
      },
      immediate: true
    },

    // 再次点选已绘制图片时调用
    async style (n) {
      if (this.hasCache) {
        await this.buildThumbnail()
      }
    },

    // 监听style可能导致imgSrcBase64格式没有转完， 所以初次绘制时监听imgSrc
    async imgSrc (n) {
      if (!this.hasCache) {
        this.set({building: true})
      }
      if (n.length > 10 && !this.hasCache){
        await this.buildThumbnail()
      }
    }
  },

  methods: {
    ...mapMutations(module.name, keys(module.mutations)),
    ...mapActions(module.name, keys(module.actions)),

    async select (index) {
      //  绘制过程中点选没有中断正在绘制的图片  导致继续绘制上张图片覆盖现选图片
      if (this.building) return;
      // 手动触发imgSrc变化调起building
      this.imgSrc = 'building'
      this.style = index
      this.position = this.templateList[index].position - 1
      this.imgSrc = await this.getBase64ByApi(this.templateList[index].imageUrl)
    },

    async buildThumbnail () {
      let noCache = (!this.posterCache[this.courseId] ||
                      (this.posterCache[this.courseId] && !this.posterCache[this.courseId][this.style]))
      // 开启构建状
      this.buildPoster()

      // 尽量确保资源加载完成
      setTimeout(async () => {
        let base64Data = ''

        if (noCache) {
          await this.initQrcode(this.style)
          this.posterCache[this.courseId] = this.posterCache[this.courseId] || {}
          const canvas = await html2canvas(this.$refs.painter)
          base64Data = canvas.toDataURL('image/jpeg', 0.8)
          this.posterCache[this.courseId][this.style] = base64Data
        } else {
          base64Data = this.posterCache[this.courseId][this.style]
        }

        // 构建状态在这里面结束
        this.updatePosterData(base64Data)
      }, noCache ? 1500 : 0)
    },

    // 金山云已设置跨域
    async getBase64 (url) {
      return new Promise(resolve => {
        const img = new Image()
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        img.crossOrigin = '*' // 'Anonymous'
        img.onload = () => {
          canvas.width = img.width
          canvas.height = img.height
          ctx.drawImage(img, 0, 0)
          resolve(canvas.toDataURL('image/png'))
        }
        img.src = url
      })
    },

    showShareNotice () {
      if (this.shareText) {
        this.$refs.notice.show()
      }
    },

    alert (msg) {
      window.alert(msg)
    }
  },

  beforeCreate () {
    this.$store.registerModule(module.name, module)
  },

  created () {
    login.getWpsSid().then(id => {
      if (!id) {
        login.login()
      } else {
        this.init(this.$route)
      }
    })
  },

  updated () {
    if (this.$refs.img) this.$refs.img.click()
  },

  beforeDestroy () {
    this.reset()
    this.$store.unregisterModule(module.name)
  },

  components: {
    notice: Notice,
    screenWrapper: ScreenWrapper,
    'ui-button': UIButton
  }
}
</script>

<style lang="scss">
@charset "UTF-8";
@import "../style/main";
// 海报的模板样式从这里引入
@import "../style/poster-style";

.poster {
  /*height: 100vh;*/
  width: 100%;
  background-image: url(../assets/images/poster-bg.png);
  overflow: hidden;

  .flag {
    position: absolute;
    top: rem(33);
    right: 0;
    z-index: 2;
    height: rem(48);
    width: rem(164);

    img {
      height: 100%;
      width: 100%;
    }

    span {
      position: absolute;
      left: rem(50);
      color: #fff;
      font-size: rem(28);
      line-height: rem(48);
    }
  }

  .stage {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;

    .poster {
      -webkit-touch-callout: none;
      position: relative;
      display: block;
      height: 70%;
      width: auto;
      margin: rem(62) auto rem(30) auto;
      border-radius: rem(12);
      box-shadow: 0 10px 70px 0 rgba(0,0,0,.5);
      overflow: hidden;
    }

    .save {
      position: fixed;
      top: 0;
      left: 0;
      z-index: 100;
      height: 60%;
      width: 100%;
      opacity: 0;
    }

    .posterMask {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 99;
      height: 100%;
      width: 100%;
      background-color: rgba($color: #000000, $alpha: .6);
      img {
        position: absolute;
        top: 30%; right: 0; left: 0;
        margin: auto;
        height: 15%;
        width: 15%;
      }
    }

    .painter {
      position: absolute;
      top: 0;
      left: 0;
      // height: 100%;
      // width: 100%;
      z-index: -1;
      background-color: #dfdfdf;
    }

    p {
      color: #fff;
      font-size: rem(24);
      text-align: center;
    }
  }

  footer {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 2;
    height: rem(200);
    width: 100%;
    background-color: #fff;

    .controller {
      height: rem(130);
      width: 100%;
      padding-top: rem(28);

      .selector {
        display: flex;
        height: rem(120);
        width: rem(486);
        margin: 0 auto;
        justify-content: space-between;

        &.compact {
          justify-content: space-around;
        }

        .option {
          position: relative;
          height: rem(110);
          width: rem(88);
          text-align: center;
          border-radius: rem(5);
          overflow: hidden;

          .optionMask {
            position: absolute;
            top: 0;
            left: 0;
            z-index: 10;
            height: 100%;
            width: 105%;
            opacity: 0;
          }

          img {
            position: absolute;
            left: 0;
            top: 0;
          }

          .thumbnail {
            width: 110%;
            height: 110%;
          }

          .border {
            height: 100%;
            width: 100%;
            z-index: 2;
          }

          &.on {
            height: rem(120);
            width: rem(94);
            margin-top: rem(-8);
            overflow: hidden;

            .thumbnail {
              width: 120%;
              height: 120%;
            }
          }
        }
      }
    }

    p {
      color: #999;
      font-size: rem(24);
      text-align: center;
      line-height: rem(33);

      b {
        color: #6D6055;

        strong {
          color: #FF6B16;
        }
      }
    }
  }
}

.wrapper-image {
  width: rem(582);
  height: rem(832);
  background: {
    size: contain;
    image: url('../assets/images/invite-poster-bg.png');
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.share {
  &-box {
    padding: use(60) 0;
  }

  &-title {
    color: #333333;
    font-size: use(36);
    line-height: use(50);
  }

  &-subtitle {
    margin-top: use(10);
    color: #999999;
    font-size: use(28);
    line-height: use(40);
  }

  &-content {
    padding: use(30);
    margin: use(30);
    color: #666666;
    font-size: use(28);
    line-height: use(40);
    background: #F5F5F5;
    border-radius: use(10);
    text-align: left;
  }

  &-button {
    width: use(300);
    margin: 0 auto;
  }
}

</style>
